<template>
  <Screen
    v-if="isUserCreatorInGroup"
    v-loading="isLoadingProject"
    back-button
    back-button-text="Project"
    :back-button-route="`/projects/${projectId}/boards`"
    :header-title="headerTitle"
    presentation-view-switch
    left
    left-auto-height
    center-big
    :user="user"
    :group-id="selectedGroupId"
    element-loading-text="Loading boards..."
  >
    <div
      slot="screen-left"
      class="project__left"
    >
      <div class="project__left-header">
        <h3 class="project__left-title">
          Boards
        </h3>
        <ElButton
          circle
          @click="toggleCreateNewBoardModal"
        >
          <i class="el-icon-plus" />
        </ElButton>
      </div>
      <div
        v-if="getBoardsByProjectId(projectId).length > 0"
        class="project__left-boards"
      >
        <Draggable
          :value="boards"
          ghost-class="board-dragged__placeholder"
          v-bind="{ animation: 150 }"
          @end="onDrop"
        >
          <div
            v-for="board in boards"
            :key="board.id"
            class="project__left-board"
            :class="[
              'project__left-board',
              { active: activeBoardId === board.id }
            ]"
            @click="activeBoardId = board.id"
          >
            <div
              v-if="hasPreviewOption(board)"
              class="project__left-board-preview"
              :style="
                `background-image: url('${getPreviewOptionImageUrl(board)}')`
              "
            />
            <div
              v-else
              class="project__left-board-preview"
            />
            <h4 class="project__left-board-title">
              {{ board.name }}
            </h4>
            <span
              class="project__left-board-delete"
              @click="showConfirmModal(board, $event)"
            >
              <Button icon>
                <Icon
                  icon="cross"
                  size="20"
                  fill="red"
                />
              </Button>
            </span>
          </div>
        </Draggable>
      </div>
      <div
        v-else
        class="project__left-boards-empty"
      >
        No boards so far...
      </div>
    </div>

    <div
      v-if="project"
      slot="screen-center"
      ref="project"
      class="project"
    >
      <section
        v-for="(board, boardsIndex) in boards"
        :key="boardsIndex"
        ref="projectBoards"
        v-loading="isLoadingBoards[board.id]"
        v-observe-visibility="
          (isVisible, entry) => fetchBoardDetailedIfNeeded(isVisible, board.id)
        "
        class="project__boards"
        :data-id="board.id"
      >
        <BoardTemplate
          create-mode
          :template-id="board.layout"
          :board-id="board.id"
        />
      </section>
      <Confirm
        v-if="showConfirmDeleteModal"
        @close="closeConfirmModal"
        @confirm="confirmDeleteBoard"
      >
        <div slot="header">
          Are you 100% Sure?
        </div>
        <div slot="body">
          <span class="project__board-delete-name">{{
            boardToDelete.boardName
          }}</span>
          will be gone for good.
        </div>
      </Confirm>
      <div
        v-if="getBoardsByProjectId(projectId).length === 0"
        class="project__board-empty"
      >
        <h2>Start me up 🤟</h2>
        <h4>Create your first board and start working</h4>
        <ElButton
          type="primary"
          @click="toggleCreateNewBoardModal"
        >
          Add first board
        </ElButton>
      </div>
      <!-- This modal is used for the template selection to create a board -->
      <StitchDialog
        :visible="showCreateNewBoardModal"
        :before-close="toggleCreateNewBoardModal"
        title="Select board layout"
      >
        <section class="project__board-templates">
          <div
            v-for="(template, index) in templates"
            :key="index"
            class="project__board-template"
            @click="createNewBoard(index)"
          >
            <img
              :src="templateBoards[index]"
              alt="template"
            >
            <div class="project__board-template-info">
              <h4 class="project__board-template-title">
                {{ template.name }}
              </h4>
            </div>
            <div class="project__board-template-arrow">
              <Icon
                icon="next"
                size="20"
                fill="#aaa"
              />
            </div>
          </div>
        </section>
        <span
          slot="footer"
          class="dialog-footer"
        >
          <ElButton @click="toggleCreateNewBoardModal">Cancel</ElButton>
        </span>
      </StitchDialog>
    </div>
    <div v-if="project">
      Loading
    </div>
  </Screen>
</template>

<script>
import { mapGetters, mapActions } from 'vuex'
import Draggable from 'vuedraggable'

import Utils from '@/services/utils'

import { Workspace } from '@/mixins/workspace'
import { Projects } from '@/mixins/projects'
import { DragAndDrop } from '@/mixins/DragAndDrop'
import { S3Trim } from '@/mixins/s3Trim'

import Confirm from '@/components/dialogs/Confirm.vue'
import Screen from '@/components/Screen.vue'
import Button from '@/components/Button.vue'
import Icon from '@/components/Icon.vue'
import BoardTemplate from '@/components/project/BoardTemplate.vue'

import { templates } from '@/resources/templates/board.json'
import { imageResolutions } from '@/constants/image'

let globalObserver = null
let globalIsScrolling = null

export default {
  name: 'Project',

  components: {
    Screen,
    Button,
    Icon,
    Confirm,
    BoardTemplate,
    Draggable
  },

  mixins: [Workspace, Projects, DragAndDrop, S3Trim],

  data: () => ({
    showCreateNewBoardModal: false,
    showConfirmDeleteModal: false,
    templates: templates,
    activeBoardId: null,
    templateBoards: [
      require('../../assets/svg/board-templates/single.svg'),
      require('../../assets/svg/board-templates/3x1.svg'),
      require('../../assets/svg/board-templates/3x2.svg'),
      require('../../assets/svg/board-templates/3x3.svg')
    ],
    boardToDelete: {},
    boardsLoaded: {},
    isLoadingProject: false,
    isLoadingBoards: {}
  }),

  computed: {
    ...mapGetters(['getStylesByBoardId', 'getBoardById']),

    /**
     * @returns {string}
     */
    imageResolution () {
      return imageResolutions.STYLE_THUMBNAIL
    },

    /**
     * @returns {boolean}
     */
    isUserCreatorInGroup () {
      return Utils.isUserCreatorInGroup(this.user, this.selectedGroupId)
    }
  },

  watch: {
    /**
     */
    activeBoardId () {
      this.$nextTick().then(() => {
        this.activateBoard(this.activeBoardId)
      })
    }
  },

  async created () {
    this.isLoadingProject = true

    await this.fetchProject({
      projectId: this.projectId
    })

    this.updateSelectedGroupIdAndSelectedSeasonId(this.project)

    this.isLoadingProject = false
  },

  updated () {
    if (!this.activeBoardId && this.$refs.projectBoards) {
      this.activeBoardId = this.getActiveBoardIdDefault(this.projectId)
    }
  },

  methods: {
    ...mapActions([
      'fetchProject',
      'fetchBoard',
      'addBoard',
      'deleteBoard',
      'updateBoardIndexStoreOnly'
    ]),

    /**
     * @param   {number} index
     *
     * @returns {object}
     */
    getBoardByListIndex (index) {
      return this.boards[index]
    },

    /**
     * @param {CustomEvent} dragEvent
     */
    async onDrop (dragEvent) {
      this.activeBoardId = await this.stopDraggingReorder({
        type: 'board',
        destinationIndex: dragEvent.newDraggableIndex,
        item: this.getBoardByListIndex(dragEvent.oldDraggableIndex),
        list: this.boards
      })

      this.activateBoard(this.activeBoardId)
    },

    /**
     * @returns {string}
     */
    getNewBoardName () {
      const boardIds = this.boards.map(board => board.id)

      return `Board ${boardIds.length + 1}`
    },

    /**
     * @returns {number}
     */
    getNewBoardIndex () {
      if (this.boards.length > 0) {
        const activeBoard = this.getBoardById(this.activeBoardId)

        return activeBoard.index + 1
      } else {
        return 0
      }
    },

    /**
     * @param {number} templateIndex
     */
    async createNewBoard (templateIndex) {
      const newIndex = this.getNewBoardIndex()
      const payload = {
        projectId: this.projectId,
        templateType: templateIndex,
        boardName: this.getNewBoardName(),
        index: newIndex
      }

      this.boards.forEach(board => {
        if (board.index >= newIndex) {
          this.updateBoardIndexStoreOnly({
            boardId: board.id,
            index: board.index + 1
          })
        }
      })

      const board = await this.addBoard(payload)

      this.activeBoardId = board.id

      this.toggleCreateNewBoardModal()
    },

    /**
     * @param   {number}        boardIndex
     *
     * @returns {number | null}
     */
    getClosestBoardId (boardIndex) {
      if (this.boards.length > 0) {
        if (boardIndex === this.boards.length) {
          return this.boards[boardIndex - 1].id
        } else {
          return this.boards[boardIndex].id
        }
      } else {
        return null
      }
    },

    /**
     * @param {string} boardId
     */
    activateBoard (boardId) {
      localStorage.setItem(`activeBoard-${this.projectId}`, boardId)

      this.$nextTick().then(() => {
        this.scrollToBoard(boardId)
      })
    },

    /**
     * @param {number} boardId
     */
    scrollToBoard (boardId) {
      const boardIndex = this.boards.findIndex(board => board.id === boardId)
      const boardElement = this.$refs.projectBoards[boardIndex]

      if (boardElement) {
        globalIsScrolling = true
        const firstBoardTop = this.$refs.projectBoards[0].offsetTop
        this.$refs.project.scrollTop = boardElement.offsetTop - firstBoardTop
      }

      this.initializeIntersectionObserverOnBoards()
    },

    /**
     * @param {boolean} isVisible
     * @param {number}  boardId
     */
    async fetchBoardDetailedIfNeeded (isVisible, boardId) {
      if (isVisible) {
        if (!this.boardsLoaded[boardId] && !this.isLoadingBoards[boardId]) {
          this.$set(this.isLoadingBoards, boardId, true)

          await this.fetchBoard({
            projectId: this.projectId,
            boardId: boardId
          })

          this.$set(this.boardsLoaded, boardId, true)
          this.$delete(this.isLoadingBoards, boardId)
        }
      }
    },

    /**
     */
    initializeIntersectionObserverOnBoards () {
      const options = {
        root: window.body,
        rootMargin: '0px',
        threshold: 1.0
      }

      if (globalObserver) {
        globalObserver.disconnect()
        globalObserver = null
      }

      globalObserver = new IntersectionObserver(entries => {
        if (globalIsScrolling) {
          globalIsScrolling = false

          return
        }

        if (entries.length > 0) {
          const boardId = entries[0].target.dataset.id
          this.activeBoardId = parseInt(boardId)
        }
      }, options)

      const boards = this.$refs.projectBoards

      if (boards) {
        boards.forEach(board => {
          globalObserver.observe(board)
        })
      }
    },

    /**
     */
    toggleCreateNewBoardModal () {
      this.showCreateNewBoardModal = !this.showCreateNewBoardModal
    },

    /**
     * @param   {object}  board
     *
     * @returns {boolean}
     */
    hasPreviewOption (board) {
      return board.preview_option && board.preview_option.file
    },

    /**
     * @param   {object}        board
     *
     * @returns {string | null}
     */
    getPreviewOptionImageUrl (board) {
      return this.s3Trim(board.preview_option.assets_file, this.imageResolution)
    },

    /**
     * @param {object} board
     * @param {Event}  clickEvent
     */
    showConfirmModal (board, clickEvent) {
      clickEvent.stopPropagation()

      this.boardToDelete.projectId = board.project_id
      this.boardToDelete.boardId = board.id
      this.boardToDelete.boardName = board.name
      this.showConfirmDeleteModal = true
    },

    /**
     */
    closeConfirmModal () {
      this.showConfirmDeleteModal = false
      this.boardToDelete = {}
    },

    /**
     */
    async confirmDeleteBoard () {
      const projectId = this.boardToDelete.projectId
      const boardId = this.boardToDelete.boardId

      const payload = {
        projectId,
        boardId
      }

      const boardIndex = this.boards.findIndex(board => board.id === boardId)

      await this.deleteBoard(payload)

      if (this.activeBoardId === boardId) {
        const newActiveBoardId = this.getClosestBoardId(boardIndex)

        if (newActiveBoardId) {
          this.activeBoardId = newActiveBoardId
        }
      }

      this.closeConfirmModal()
    }
  }
}
</script>

<style lang="scss" scoped>
$height-project-left-board: 60px;
$size-project-left-preview: 30px;
$height-board-preview-list: calc(100% - #{$spacer-unit-x2});
$height-board-list: 90%;
$gutter-template-selector-option: spacing(1);
$width-board-title: calc(100% - #{$size-project-left-preview});
$project-gutter: $spacer-unit / 4;

.project {
  @include size(100%);
  @include scroll-y-smooth();

  display: flex;
  flex-direction: column;
  align-items: center;
}

.project__left {
  display: flex;
  flex-direction: column;
  height: 100%;
  overflow: hidden;
}

.project__left-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  height: $height-header;
  margin-bottom: $spacer-unit;
}

.project__left-boards {
  @include scroll-y-smooth();

  position: relative;
  display: flex;
  flex-direction: column;
  height: 100%;
  max-height: $height-board-preview-list;
  padding: 0 $spacer-unit 0;
}

.project__left-board {
  @include size(100%, $height-project-left-board);

  position: relative;
  display: flex;
  align-items: center;
  margin-bottom: $spacer-unit-x2;
  padding: $spacer-unit;
  background-color: $white;
  background-repeat: no-repeat;
  background-position: center center;
  background-size: contain;
  border: 2px solid transparent;
  border-radius: $border-radius-default;
  box-shadow: 0 2px 5px 0 rgba(0, 0, 0, 0.2);
  cursor: pointer;

  &.active {
    border: 2px solid $blue;
  }

  .project__left-board-preview {
    @include size($size-project-left-preview);

    display: inline-flex;
    margin-right: $spacer-unit;
    background-image: url('../../assets/images/shirt-placeholder.png');
    background-repeat: no-repeat;
    background-position: center;
    background-size: contain;
  }

  .project__left-board-title {
    width: $width-board-title;
    overflow: hidden;
    color: $grey-light;
    white-space: nowrap;
    text-overflow: ellipsis;
  }

  .project__left-board-delete {
    position: absolute;
    top: $project-gutter;
    right: $project-gutter;
    opacity: 0;

    button {
      background: $grey-blue-light;
      border-radius: $border-radius-round;
    }
  }

  &:hover {
    .project__left-board-delete {
      opacity: 1;
    }
  }

  h4 {
    text-transform: uppercase;
  }
}

.project__left-boards-empty {
  width: 100%;
  margin-bottom: $gutter;
  text-align: center;
}

.project__boards {
  flex: 1 0 auto;
  width: 100%;
  height: $height-board-list;
  max-height: $max-height-list-item;
  margin-top: $gutter;
  padding: $gutter $spacer-unit-x4;
  background: $white;
  border-radius: $border-radius-default;

  &:last-of-type {
    margin-bottom: $gutter;
  }
}

.project__board-templates {
  @include scroll-y-smooth();

  display: flex;
  flex-direction: column;
  align-items: center;
  width: 100%;
}

.project__board-template {
  align-items: center;
  width: $width-template-item;
  height: $height-template-item;
  margin: $gutter-template-selector-option;
  padding: $gutter-template-selector-option;
  background-color: $off-white;
  border: 2px solid $off-white;
  border-radius: $border-radius-default;

  &:hover {
    border: 2px solid $blue;
    cursor: pointer;
  }

  img {
    position: relative;
    left: $position-center;
    height: 100%;
    margin-right: $spacer-unit-x2;
    transform: translateX(-50%);

    @include respond-to('l') {
      position: static;
      float: left;
      transform: none;
    }
  }

  .project__board-template-info {
    display: none;
    float: left;
    height: 100%;

    @include respond-to('l') {
      display: block;
    }

    .project__board-template-title {
      position: relative;
      top: $position-center;
      display: block;
      color: $grey;
      transform: translateY(-50%);
    }
  }

  .project__board-template-arrow {
    display: none;
    float: right;
    height: 100%;
    font-size: $font-size-extra-large;
    line-height: 76px;

    @include respond-to('l') {
      display: block;
    }
  }
}

.project__board-empty {
  @include absolute-center;

  text-align: center;

  h2 {
    margin-bottom: $margin-small;
    font-size: $font-size-extra-large;
  }

  h4 {
    margin-bottom: $margin-small;
    color: $grey-light;
  }
}

.project__board-delete-name {
  color: $grey-blue;
  text-transform: uppercase;
}

.board-dragged__placeholder {
  @include element-dragged-placeholder;
}
</style>
